import { caseworkLocalToWorldTransformFromHostPath } from "building-generation/elements/caseworks/caseworkLocalTransform";
import { rotateCaseworkHostPath } from "building-generation/elements/caseworks/caseworkRotation";
import { cornerFeatureAtPathStart } from "building-generation/elements/caseworks/caseworkShape";
import {
  addFillerPanelAlongPath,
  cabinetAlongPath,
  extrudeRectAlongPathExtended,
  HALF_CABINET_GAP,
  makeCabinetDoors,
  sweepClosetShelfRod,
} from "building-generation/elements/caseworks/caseworkUtils";
import type { CaseworkDescriptor } from "building-types/casework/caseworkDescriptor";
import type { Casework, CaseworkTagPlacement } from "building-types/casework/caseworkTypes";
import {
  CabinetType,
  CaseworkShapeType,
  ShelvingType,
  UpperCabinetSubType,
} from "building-types/casework/caseworkTypesEnum";
import {
  CaseworkHorizontalAlignment,
  CaseworkLateralAlignment,
  CaseworkLineStyle,
  CaseworkVerticalAlignment,
  CaseworkVerticalDatumAnchor,
} from "building-types/casework/wallCaseworkTypes";
import type { WallCasework } from "building-types/casework/wallCaseworkTypes";
import { WallLateralDatumAnchorAll } from "building-types/datums/datumsTypes";
import type { FloorAssembly } from "building-types/floors/floorAssemblyTypes";
import type { CaseworkHandle } from "building-types/handles/docEntityHandleTypes";
import type { RoomIndex } from "building-types/rooms/roomIndex";
import type { RoomOutlines } from "building-types/rooms/roomOutlineTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import { BuildingGenerationIssueType } from "building-types/settings/buildingGenerationIssue";
import type { CaseworkSettings } from "building-types/settings/caseworkSettings";
import type { InteriorFloorMaterialOverride } from "building-types/settings/floorMaterialSettings";
import type { WallIndex } from "building-types/walls/wallIndex";
import type { WallMassSystem } from "building-types/walls/wallSytemTypes";
import type { RWTable, Table } from "common/data/table";
import { ensureDefined } from "common/utils/assertOfType";
import { assertUnreachable } from "common/utils/assertUnreachable";
import uid from "common/utils/uid";
import type { ResolvedCaseworkShape } from "doc/elements/caseworkSettingsResolution";
import { resolveCaseworkFromSettings, resolveCaseworkShape } from "doc/elements/caseworkSettingsResolution";
import { floorAssemblyTop } from "doc/elements/floor/floorAssembly";
import type { WallLayerExtentsCache } from "doc/elements/openings/openingWallLayersTypes";
import {
  entityParamFromSpecificWallLayer,
  isEdgeHostedEntityFromEnd,
} from "doc/elements/openings/openingWallLayersUtils";
import { assertIsNotFormula } from "doc/formula/formulaUtils";
import { entityHandlesIfRoomLayoutInstanceOverride } from "doc/rooms/layout/roomLayoutTools";
import { isDeprecatedCasework, isFixedCasework, resolvedCabinetShape } from "doc/utils/caseworkDescriptorUtils";
import { keyMeasureNew } from "doc/utils/dataSequenceTools";
import { aabb2ByPolyLine2s } from "geom/aabb2";
import { aabb3New } from "geom/aabb3Types";
import type { BRep } from "geom/brep";
import { type Extrusion } from "geom/brep";
import { DIST_EPSILON } from "geom/core";
import { line2Eval } from "geom/line2";
import { planeNewXY } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import {
  polyLine2Centroid,
  polyLine2Edge,
  polyLine2EdgeCount,
  polyLine2EdgeEval,
  polyLine2EdgeNormal,
  polyLine2End,
  polyLine2Extend,
  polyLine2Length,
  polyLine2Start,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { caseworkNew } from "../elements/caseworks/casework";
import {
  entityLateralOffsetFromWallDatum,
  getFromOrAddToWallLayerExtentsCache,
} from "../elements/openings/openingWallLayers";

export function makeCasework({
  wallCasework,
  wallLayerExtentsCache,
  wallIndex,
  wallMasses,
  room,
  roomIndex,
  roomOutlines,
  roomFloorAssemblies,
  floorMaterial,
  caseworkCatalog,
  issues,
  caseworkSettings,
  mirror,
  wallCaseworkId,
  forEveryCasework,
  outputCasework,
}: {
  wallCasework: WallCasework;
  wallLayerExtentsCache: WallLayerExtentsCache;
  wallIndex: WallIndex;
  wallMasses: Table<WallMassSystem>;
  room: Room;
  roomIndex: RoomIndex;
  roomOutlines: RoomOutlines;
  roomFloorAssemblies: Table<FloorAssembly>;
  floorMaterial?: InteriorFloorMaterialOverride;
  caseworkCatalog: Table<CaseworkDescriptor>;
  issues: BuildingGenerationIssue[];
  caseworkSettings: CaseworkSettings;
  mirror: boolean;
  wallCaseworkId: string;
  forEveryCasework: ((caseworkId: string, casework: Casework, disabled: boolean) => void) | null;
  outputCasework: RWTable<Casework>;
}) {
  const {
    type,
    name,
    paramAnchor,
    depth,
    height,
    lateralOffset,
    verticalOffset,
    cabinetDoorsOnLeft: cabinetDoorsOnLeftPath,
    cabinetDoorsOnRight: cabinetDoorsOnRightPath,
    notes,
    lineStyle,
    material,
    caseworkType,
    blindPanelSide,
    blindPanelExtension,
    adjacentCabinetFiller,
    adjacentCabinetFillerWidth,
    handleOverride,
    entityInstanceType,
    closetShelfRod,
    paletteType,
  } = wallCasework;

  assertIsNotFormula(height);
  assertIsNotFormula(depth);
  assertIsNotFormula(lateralOffset);
  assertIsNotFormula(verticalOffset);

  const paramFromEnd = isEdgeHostedEntityFromEnd({ paramAnchor });

  const { handle, handleAuxiliary } = entityHandlesIfRoomLayoutInstanceOverride(
    { type: "CASEWORK", id: wallCaseworkId } as CaseworkHandle,
    handleOverride,
  );

  // Resolve the shape up front and never read the raw `depth`/`height`/`verticalOffset`/`material`
  // again: `depth`/`height`/`verticalOffset`/`material` are optional on the doc entity, so every
  // later arithmetic use has to see a number.
  const resolvedShape: ResolvedCaseworkShape = resolveCaseworkShape(
    { caseworkType, depth, height, verticalOffset, material, paletteType },
    caseworkSettings,
  );

  let resolvedDepth = resolvedShape.depth;
  let resolvedHeight = resolvedShape.height;
  let resolvedWidth = isFixedCasework(wallCasework) ? wallCasework.width : undefined;
  let resolvedMaterial = resolvedShape.material;
  let resolvedParamStart = isFixedCasework(wallCasework) ? wallCasework.param : wallCasework.placementStart.param;
  let resolvedParamStartNormalized = isFixedCasework(wallCasework)
    ? wallCasework.paramNormalized
    : wallCasework.placementStart.paramNormalized;
  let resolvedParamEnd = isFixedCasework(wallCasework) ? undefined : wallCasework.placementEnd.param;
  let resolvedParamEndNormalized = isFixedCasework(wallCasework)
    ? undefined
    : wallCasework.placementEnd.paramNormalized;
  let resolvedLateralOffset = lateralOffset;
  /**
   * Where the cabinet is pinned to the wall, for {@link rotateCaseworkHostPath} to turn it about.
   * Only the non-deprecated branch below resolves one, which is also the only branch that can rotate.
   */
  let anchorParamAlongPath = 0;
  /**
   * The lateral half of that anchor: the wall datum the placement pins the box to, captured before
   * `lateralAlignment` slides the box off it — the perpendicular twin of `anchorParamAlongPath`.
   */
  let anchorLateralOffset = 0;
  let resolvedVerticalOffset = isDeprecatedCasework(wallCasework)
    ? floorAssemblyTop(roomFloorAssemblies[room.id], room.level) + resolvedShape.verticalOffset
    : resolvedShape.verticalOffset;

  if (isDeprecatedCasework(wallCasework)) {
    if (wallCasework.heightFromCeiling) {
      resolvedHeight = room.level.topOfWall - resolvedShape.height - resolvedShape.verticalOffset;
    }
  }

  let countertopMaterial = resolvedShape.countertopMaterial;
  let resolvedPaletteType = resolvedShape.paletteType;
  // The box shape — kept apart from `resolvedShape` above, which is the resolved dimensions.
  let cabinetShape = wallCasework.shape;

  let blindPanel = false;
  let includeHardware = resolvedShape.includeHardware;
  let hardwareVerticalPositionParam = resolvedShape.hardwareVerticalPositionParam;

  // tags
  let tagText = wallCasework.tag;
  let tagOffset = wallCasework.tagOffset;
  let roomEdgeIds: string[] = [];

  // get shape values from catalog item
  if (isFixedCasework(wallCasework) && wallCasework.catalogDescriptorId) {
    const { catalogDescriptorId } = wallCasework;
    const catalogDescriptor: CaseworkDescriptor = caseworkCatalog[catalogDescriptorId];
    if (!catalogDescriptor) {
      issues.push({
        type: BuildingGenerationIssueType.UNKNOWN_CASEWORK_TYPE,
        caseworkType: catalogDescriptorId,
        handle,
      });
      return undefined;
    }

    // Follows the descriptor's type, not the entity's `caseworkType`: docs exist where the two
    // disagree, and every branch below builds the box from the descriptor.
    hardwareVerticalPositionParam = resolveCaseworkFromSettings(
      catalogDescriptor.type,
      caseworkSettings,
    ).hardwareVerticalPositionParam;

    cabinetShape = resolvedCabinetShape(wallCasework, catalogDescriptor);

    if (tagText === undefined) {
      tagText = catalogDescriptor.tag;
    }
    if (tagOffset === undefined) {
      tagOffset = catalogDescriptor.tagOffset;
    }

    // An explicit doc `material` is a user override and wins over the settings, matching the
    // non-catalog path in `resolveCaseworkShape`. Catalog casework used to ignore it only because
    // `resolvedMaterial` was dead code — the return block passed the raw doc value straight through.
    // The palette follows the material: a PaletteMaterialSlot only means something inside its palette,
    // so taking the settings palette while keeping the entity's slot would read it against the wrong one.
    const inheritsMaterial = material === undefined;

    switch (catalogDescriptor.type) {
      case CabinetType.BASE_CABINET: {
        resolvedWidth = catalogDescriptor.width;
        resolvedHeight = caseworkSettings.topOfCountertop;
        resolvedDepth = caseworkSettings.baseCabinetDepth;
        if (inheritsMaterial) {
          resolvedMaterial = caseworkSettings.baseCabinetMaterial;
          resolvedPaletteType = caseworkSettings.paletteType;
        }
        countertopMaterial = caseworkSettings.countertopMaterial;
        includeHardware = caseworkSettings.hardware;
        break;
      }
      case CabinetType.UPPER_CABINET: {
        switch (catalogDescriptor.subType) {
          case UpperCabinetSubType.REFRIGERATOR: {
            resolvedVerticalOffset += caseworkSettings.bottomOfRefrigeratorUpperCabinets;
            resolvedDepth = caseworkSettings.upperRefrigeratorCabinetDepth;
            break;
          }
          case UpperCabinetSubType.MICROWAVE: {
            resolvedVerticalOffset += caseworkSettings.bottomOfMicrowaveUpperCabinets;
            resolvedDepth = caseworkSettings.upperMicrowaveCabinetDepth;
            break;
          }
          case UpperCabinetSubType.WALL_BLIND_SQUARE_CORNER: {
            blindPanel = true;
            resolvedVerticalOffset += caseworkSettings.bottomOfUpperCabinets;
            resolvedDepth = caseworkSettings.upperCabinetDepth;
            break;
          }
          default: {
            resolvedVerticalOffset += caseworkSettings.bottomOfUpperCabinets;
            resolvedDepth = caseworkSettings.upperCabinetDepth;
            break;
          }
        }
        resolvedWidth = catalogDescriptor.width;
        resolvedHeight = catalogDescriptor.height;
        includeHardware = caseworkSettings.hardware;
        if (inheritsMaterial) {
          resolvedMaterial = caseworkSettings.upperCabinetMaterial;
          resolvedPaletteType = caseworkSettings.paletteType;
        }
        break;
      }
      case CabinetType.TALL_CABINET: {
        resolvedWidth = catalogDescriptor.width;
        resolvedHeight = catalogDescriptor.height;
        resolvedDepth = caseworkSettings.tallCabinetDepth;
        if (inheritsMaterial) {
          resolvedMaterial = caseworkSettings.tallCabinetMaterial;
          resolvedPaletteType = caseworkSettings.paletteType;
        }
        includeHardware = caseworkSettings.hardware;
        break;
      }
      case CabinetType.FILLER_PANEL: {
        resolvedWidth = catalogDescriptor.width;
        resolvedDepth = caseworkSettings.baseCabinetDepth;
        if (inheritsMaterial) {
          resolvedMaterial = caseworkSettings.baseCabinetMaterial;
          resolvedPaletteType = caseworkSettings.paletteType;
        }
        break;
      }
      case ShelvingType.CLOSET_SHELF: {
        resolvedWidth = catalogDescriptor.width;
        resolvedHeight = 0.75;
        resolvedVerticalOffset += caseworkSettings.topOfUpperShelf - resolvedHeight;
        resolvedDepth = catalogDescriptor.shelfDepth;
        break;
      }
    }
  }

  /**
   * The `ANGLED` cut or the `L_SHAPED` notch — a cabinet has one `shape`, so at most one is defined.
   * A blind panel already claims `blindPanelSide`, so a cabinet with both keeps the panel and skips this.
   */
  const angledShape = !blindPanel && cabinetShape?.type === CaseworkShapeType.ANGLED ? cabinetShape : undefined;
  // The schema allows a short `frontWidths`, and an absent width leaves that edge whole — no cut.
  const frontCornerMiter = angledShape
    ? {
        frontFaceWidth: angledShape.frontWidths.at(0),
        endFaceWidth: angledShape.frontWidths.at(1),
        side: wallCasework.blindPanelSide,
        reverse: (angledShape.reverse ?? false) !== mirror,
      }
    : undefined;

  /** The 45° slice off the back corner, which shares the front cut's end of the cabinet. */
  const backCornerMiter =
    angledShape && (angledShape.rearWidth ?? 0) > 0
      ? {
          legLength: angledShape.rearWidth ?? 0,
          side: wallCasework.blindPanelSide,
          reverse: (angledShape.reverse ?? false) !== mirror,
        }
      : undefined;

  const notchShape = !blindPanel && cabinetShape?.type === CaseworkShapeType.L_SHAPED ? cabinetShape : undefined;
  const notchFrontLeg = notchShape?.frontWidths.at(0) ?? 0;
  const notchEndLeg = notchShape?.frontWidths.at(1) ?? 0;
  const frontCornerNotch =
    notchShape && notchFrontLeg > 0 && notchEndLeg > 0
      ? {
          frontNotchLength: notchFrontLeg,
          endNotchLength: notchEndLeg,
          side: wallCasework.blindPanelSide,
          reverse: (notchShape.reverse ?? false) !== mirror,
        }
      : undefined;

  const wallStripPaths: PolyLine2[] = [];

  if (isDeprecatedCasework(wallCasework)) {
    roomEdgeIds = type === "FIXED" ? [wallCasework.edgeId] : wallCasework.edgeIds;
    const wallStrips = wallIndex.wallStripsByPredicate(room, (wall) => {
      const edgeIdx = wallIndex.wallParentRoomEdgeIndex(room, wall);
      return roomEdgeIds.includes(room.edgeIds[edgeIdx]);
    });
    wallStrips.forEach((strip) => wallStripPaths.push(strip.baselineMaxOffset(true)));
  } else {
    roomEdgeIds = [wallCasework.edgeId];
    const edgeIndex = room.edgeIds.indexOf(wallCasework.edgeId);
    if (edgeIndex === -1) {
      console.warn(`edgeId is invalid for roomId=${room.id}`);
      return null;
    }
    const wallMassSystem = wallMasses[room.level.id];
    const {
      edgeMinTable,
      edgeMaxTable,
      largestInsideOffset,
      mostInwardLayerIntoRoom: mostInwardLayerIntoRoom,
      roomEdgeLength,
    } = getFromOrAddToWallLayerExtentsCache(room, edgeIndex, wallLayerExtentsCache, roomIndex, wallMassSystem);

    const { param, layerMin, layerMax } = entityParamFromSpecificWallLayer({
      placement: wallCasework,
      largestOffset: largestInsideOffset,
      edgeLength: roomEdgeLength,
      mirror,
      mostInwardLayerIntoRoom,
      edgeMinTable,
      edgeMaxTable,
    });

    const outline = room.outline;
    if (!outline) {
      return undefined;
    }

    // create adjusted room edge as host
    const roomEdge = polyLine2Edge(outline, edgeIndex);
    const [rsx, rsy] = polyLine2Start(roomEdge);
    const [rex, rey] = polyLine2End(roomEdge);

    wallStripPaths.push(
      polyLine2New([
        ...line2Eval(rsx, rsy, rex, rey, layerMin, false),
        ...line2Eval(rsx, rsy, rex, rey, layerMax, false),
      ]),
    );

    resolvedLateralOffset = entityLateralOffsetFromWallDatum({
      room,
      edgeIndex,
      roomOutlines,
      wallMassSystem,
      placement: {
        ...wallCasework,
        lateralDatum: wallCasework.useWallStripMaxOffset
          ? WallLateralDatumAnchorAll.WALL_OFFSET
          : wallCasework.lateralDatum,
      },
      resolvedParam: param,
    });

    // Captured before alignment, for the same reason the param below is: the cabinet turns about the
    // datum the user pinned it to, not about whichever face the alignment happens to put there.
    anchorLateralOffset = resolvedLateralOffset;

    // adjust lateral offset for alignment
    switch (wallCasework.lateralAlignment) {
      case CaseworkLateralAlignment.INSIDE_FACE:
        break;
      case CaseworkLateralAlignment.CENTERLINE:
        resolvedLateralOffset -= resolvedDepth / 2;
        break;
      case CaseworkLateralAlignment.OUTSIDE_FACE:
        resolvedLateralOffset -= resolvedDepth;
        break;
      default:
        assertUnreachable(wallCasework.lateralAlignment);
    }

    // adjust param for alignment
    // TODO: need to convert param to imperial mode before doing this
    if (resolvedParamStartNormalized) {
      resolvedParamStart *= layerMax - layerMin;
      resolvedParamStartNormalized = false;
    }
    // Captured before alignment slides the box off it: rotation turns the cabinet about where the
    // user placed it, not about whichever corner the alignment happens to put there. With
    // `anchorLateralOffset` above, these are the two halves of the point a prop of the same
    // placement would turn about — see `propAtPoint` in `doc/rooms/propPlacement`.
    anchorParamAlongPath = resolvedParamStart;
    switch (wallCasework.horizontalAlignment) {
      case CaseworkHorizontalAlignment.LEFT:
        resolvedParamStart -= resolvedWidth ? resolvedWidth : 0;
        break;
      case CaseworkHorizontalAlignment.CENTER:
        resolvedParamStart -= resolvedWidth ? resolvedWidth / 2 : 0;
        break;
      case CaseworkHorizontalAlignment.RIGHT:
        break;
      default:
        assertUnreachable(wallCasework.horizontalAlignment);
    }

    // adjust vertical offset for alignment
    switch (wallCasework.verticalAlignment) {
      case CaseworkVerticalAlignment.TOP:
        resolvedVerticalOffset -= resolvedHeight;
        break;
      case CaseworkVerticalAlignment.CENTER:
        resolvedVerticalOffset -= resolvedHeight / 2;
        break;
      case CaseworkVerticalAlignment.BOTTOM:
        break;
      default:
        assertUnreachable(wallCasework.verticalAlignment);
    }

    const { level } = room;

    // adjust vertical offset for datum
    switch (wallCasework.verticalDatum) {
      case CaseworkVerticalDatumAnchor.LEVEL_FLOOR_ELEVATION:
        resolvedVerticalOffset += level.floorElevation;
        break;
      case CaseworkVerticalDatumAnchor.LEVEL_CEILING_HEIGHT:
        resolvedVerticalOffset += level.floorElevation + level.topOfWall;
        break;
      case CaseworkVerticalDatumAnchor.LEVEL_GRADE:
        resolvedVerticalOffset += level.floorElevation + level.levelDatumsGenerated.grade;
        break;
      case CaseworkVerticalDatumAnchor.ROOM_FLOOR_ELEVATION:
        const floorAssembly = roomFloorAssemblies[room.id];
        resolvedVerticalOffset += floorAssemblyTop(floorAssembly, level);
        break;
      default:
        assertUnreachable(wallCasework.verticalDatum);
    }
  }
  // make casework geometry
  function makeCaseworkGeometry(hostPath: PolyLine2) {
    // Rotation is applied to the host before anything is built from it, so the box, its doors, its
    // plan loops and its local transform all turn together — see `rotateCaseworkHostPath`.
    const stripPath = rotateCaseworkHostPath(hostPath, {
      rotationDegrees: isDeprecatedCasework(wallCasework) ? 0 : wallCasework.rotationDegrees ?? 0,
      anchorParam: anchorParamAlongPath,
      anchorOffset: -anchorLateralOffset,
      paramFromEnd,
      mirror,
    });

    if (isFixedCasework(wallCasework)) {
      const definitiveResolvedWidth = resolvedWidth ?? 0;
      resolvedParamEnd = resolvedParamStartNormalized
        ? resolvedParamStart * polyLine2Length(stripPath) + definitiveResolvedWidth
        : resolvedParamStart + definitiveResolvedWidth;
      resolvedParamEndNormalized = false;
    }

    const decorativeExtrusions: Extrusion[] = [];
    const decorativeBReps: BRep[] = [];
    let wallSweepExtrusions: Extrusion[] = [];
    let countertopExtrusions: Extrusion[] = [];
    let planLoops: PolyLine2[];
    let footprintLoops: PolyLine2[] | undefined;
    let adjustedPath: PolyLine2;
    let innerPath: PolyLine2;
    /** The stretch of the front the doors hang on — the shaped face(s) alone, once a corner is cut. */
    let cabinetDoorPath: PolyLine2;
    let cabinetDoorBasePlane: Plane;
    let cabinetDoorHeight: number;
    let closetShelfRodLine: PolyLine2 | undefined;
    let caseworkWidth = resolvedWidth;

    // main casework extrusions
    if (caseworkType === CabinetType.BASE_CABINET || caseworkType === CabinetType.TALL_CABINET) {
      const { toeKickHeight, toeKickDepth, countertopThickness, countertopOverhang } = caseworkSettings;
      const isTallCabinet = caseworkType === CabinetType.TALL_CABINET;

      const baseCabinet = cabinetAlongPath({
        stripPath,
        finalBaseHeight: resolvedVerticalOffset,
        thickness: resolvedHeight,
        depth: resolvedDepth,
        lateralOffset: resolvedLateralOffset,
        paramStart: resolvedParamStart,
        paramStartNormalized: resolvedParamStartNormalized,
        paramEnd: resolvedParamEnd,
        paramEndNormalized: resolvedParamEndNormalized,
        countertopThickness: isTallCabinet ? 0 : countertopThickness,
        countertopOverhang,
        toeKickHeight,
        toeKickDepth,
        paramAnchor,
        mirror,
        isDeprecatedPlacement: isDeprecatedCasework(wallCasework),
        frontCornerMiter,
        frontCornerNotch,
        backCornerMiter,
      });
      wallSweepExtrusions = baseCabinet.extrusions;
      countertopExtrusions = baseCabinet.countertopExtrusions;
      planLoops = baseCabinet.planLoops;
      footprintLoops = baseCabinet.footprintLoops;
      adjustedPath = baseCabinet.path;

      caseworkWidth = polyLine2Length(adjustedPath);

      innerPath = baseCabinet.innerPath;
      cabinetDoorPath = baseCabinet.doorPath;
      cabinetDoorBasePlane = baseCabinet.cabinetDoorBasePlane;
      cabinetDoorHeight = baseCabinet.cabinetDoorHeight;
    } else {
      const casework = extrudeRectAlongPathExtended({
        stripPath,
        baseHeight: resolvedVerticalOffset,
        thickness: resolvedHeight,
        depth: paramFromEnd ? -resolvedDepth : resolvedDepth,
        offset: -resolvedLateralOffset,
        paramStart: resolvedParamStart,
        paramStartNormalized: resolvedParamStartNormalized,
        paramEnd: resolvedParamEnd,
        paramEndNormalized: resolvedParamEndNormalized,
        reverse: paramFromEnd,
        reverseExtends: mirror,
        isDeprecatedPlacement: isDeprecatedCasework(wallCasework),
        frontCornerMiter,
        frontCornerNotch,
        backCornerMiter,
      });

      wallSweepExtrusions = [casework.extrusion];

      planLoops = casework.loops;
      adjustedPath = casework.path;
      innerPath = casework.innerPath;
      cabinetDoorPath = casework.doorPath;
      caseworkWidth = polyLine2Length(adjustedPath);
      cabinetDoorBasePlane = planeNewXY(0, 0, resolvedVerticalOffset + HALF_CABINET_GAP);
      cabinetDoorHeight = resolvedHeight - 2 * HALF_CABINET_GAP;

      if (closetShelfRod) {
        closetShelfRodLine = casework.closetShelfRodPath;
        decorativeBReps.push(sweepClosetShelfRod(closetShelfRodLine, resolvedVerticalOffset - 2));
      }
    }

    // blind corner
    if (blindPanel) {
      // Sizing the panel to `resolvedDepth` — the depth the cabinet box was actually built to — is the
      // correct geometry, but it is a geometry change, so it gets its own ticket and baseline sweep.
      // re-asserted because the outer narrowing does not reach into this closure
      assertIsNotFormula(depth);
      const blindPanelDepth = depth ?? resolvedDepth;
      const accessibleCabinetWidth = caseworkWidth - blindPanelDepth;
      const blindPanelLength = blindPanelExtension ? blindPanelExtension + blindPanelDepth : blindPanelDepth;

      if (adjacentCabinetFiller) {
        addFillerPanelAlongPath({
          cabinetDoorsOnLeftPath,
          cabinetDoorsOnRightPath,
          blindPanelSide,
          path: adjustedPath,
          innerPath,
          paramFromEnd,
          accessibleCabinetWidth,
          adjacentCabinetFillerWidth,
          finalBaseHeight: resolvedVerticalOffset,
          thickness: resolvedHeight,
          extrusions: decorativeExtrusions,
        });
      }

      // update door paths
      const pathReductionLength = blindPanelLength - HALF_CABINET_GAP * 2; //assumes the door overlaps blind panel extension by HALF_CABINET_GAP
      const blindPanelAtPathStart = cornerFeatureAtPathStart(blindPanelSide, paramFromEnd);
      const blindPanelLength1 = blindPanelAtPathStart ? -pathReductionLength : 0;
      const blindPanelLength2 = blindPanelAtPathStart ? 0 : -pathReductionLength;

      polyLine2Extend(adjustedPath, blindPanelLength2, blindPanelLength1);
      // A blind panel excludes a corner shape, so this is still the full front — shortening it is
      // what pulls the doors clear of the panel.
      polyLine2Extend(cabinetDoorPath, blindPanelLength1, blindPanelLength2);
    }

    // casework doors and hardware
    makeCabinetDoors({
      path: adjustedPath,
      doorPath: cabinetDoorPath,
      basePlane: cabinetDoorBasePlane,
      height: cabinetDoorHeight,
      cabinetDoorsOnLeftPath,
      cabinetDoorsOnRightPath,
      extrusionOutput: decorativeExtrusions,
      paramFromEnd,
      doorStyle: caseworkSettings.doorStyle,
      includeHardware,
      hardwareVerticalPositionParam,
    });

    // notes
    const [cx, cy] = ensureDefined(polyLine2Centroid(planLoops[0]));
    const flip = mirror ? -1 : 1;
    const finalNotes = notes
      ? notes.map((n) => {
          return {
            note: n.note,
            orientation: n.orientation,
            position: [flip * n.position[0] + cx, n.position[1] + cy],
          };
        })
      : [];

    const { minX = 0, minY = 0, maxX = DIST_EPSILON, maxY = DIST_EPSILON } = aabb2ByPolyLine2s(planLoops) ?? {};
    const minZ = resolvedVerticalOffset;
    const maxZ = minZ + resolvedHeight;
    const aabb3 = aabb3New(minX, minY, minZ, maxX, maxY, maxZ);

    const newCaseworkId = uid();
    const localToWorldTransform = caseworkLocalToWorldTransformFromHostPath(adjustedPath, [
      ...wallSweepExtrusions,
      ...decorativeExtrusions,
      ...countertopExtrusions,
      ...decorativeBReps,
    ]);

    // TODO: do not use legacy procure types in building generation
    const caseworkMeasures = {
      height: keyMeasureNew(resolvedHeight, "INCHES"),
      width: keyMeasureNew(caseworkWidth, "INCHES"),
      depth: keyMeasureNew(resolvedDepth, "INCHES"),
      count: keyMeasureNew(1),
    };

    const tagPlacements: CaseworkTagPlacement[] = [];
    if (tagText) {
      for (let i = 0; i < polyLine2EdgeCount(adjustedPath); i++) {
        const [x, y] = polyLine2EdgeEval(adjustedPath, i, 0.5, true);
        const [nx, ny] = polyLine2EdgeNormal(adjustedPath, i, !paramFromEnd);
        const offset = resolvedDepth / 2 + (tagOffset ?? 0);
        const mx = x + nx * offset;
        const my = y + ny * offset;

        tagPlacements.push({
          position: [mx, my],
          normal: [nx, ny],
        });
      }
    }

    return caseworkNew({
      id: newCaseworkId,
      room,
      planDash: lineStyle === CaseworkLineStyle.DASHED ? planLoops : undefined,
      planPoche: lineStyle === CaseworkLineStyle.POCHE ? planLoops : undefined,
      planHidden: lineStyle === CaseworkLineStyle.NONE ? planLoops : undefined,
      planSolid: !lineStyle || lineStyle === CaseworkLineStyle.SOLID ? planLoops : undefined,
      closetShelfRodLines: closetShelfRodLine ? [closetShelfRodLine] : [],
      footprintOutlines: footprintLoops ?? planLoops,
      aabb3,
      wallStripPath: stripPath,
      depth: resolvedDepth,
      floorMaterial,
      caseworkMeasures /** @deprecated */,
      wallSweepExtrusions,
      decorativeExtrusions,
      countertopExtrusions,
      decorativeBReps,
      planNotes: finalNotes,
      handle,
      handleAuxiliary,
      name,
      material: resolvedMaterial,
      countertopMaterial,
      entityInstanceType,
      paletteType: resolvedPaletteType ?? paletteType,
      tag: tagText ? { text: tagText, offset: tagOffset ?? 0 } : undefined,
      tagPlacements: tagPlacements.length > 0 ? tagPlacements : undefined,
      roomEdgeIds,
      localToWorldTransform,
    });
  }

  for (const path of wallStripPaths) {
    const cwk = makeCaseworkGeometry(path);

    outputCasework[cwk.id] = cwk;

    if (forEveryCasework) {
      if (cwk.handle) {
        forEveryCasework(cwk.handle.id, cwk, !!wallCasework.disabled);
      }
    }
  }

  return undefined;
}
